import { Injectable } from "@angular/core";
import { IPaginationRequestBaseModel } from "../models/generic/pagination/pagination-request.base.model";
import { IPaginationResponseModel } from "../models/generic/pagination/pagination-response.base.model";
import { ISavedAddressModel } from "../models/saved-address/saved-address.model";
import { ISavedAddressDtoModel } from "../models/saved-address/dto/saved-address-dto.model";
import { ISavedAddressPostDtoModel } from "../models/saved-address/dto/saved-address-post-dto.model";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { SavedAddressMapping } from "../mappings/saved-address.mapping";
import { EnvironmentService } from "./environment.service";
import { ApplicationContextService } from "./application-context.service";
import { ICustomerResultModel } from "../models/customer/customer-result.model";
import { CoreService } from "./customer-core.service";
import { CustomError } from "../error-handlers/custom-error";

@Injectable({
   providedIn: 'root'
})
export class SavedAddressService {
   private mapping: SavedAddressMapping;
   constructor(private readonly httpClient: HttpClient, public envService: EnvironmentService, private applicationContext: ApplicationContextService, private customerService: CoreService) { 
      this.mapping = new SavedAddressMapping();
   }

   private path(){

      if(this.applicationContext.isPublic()){
         return `${this.envService.settings.publicApiUrl}/Customers/addresses`;
      }

      return `${this.envService.settings.apiUrl}/Addresses`;
   }

   getAll(paginationModel: IPaginationRequestBaseModel): Observable<IPaginationResponseModel<ISavedAddressModel>> {
      throw new Error("Method not implemented.");
   }

   public delete(ids: number[], customerId: string): Observable<any> {
      const httpOptions = {
         headers: new HttpHeaders({ 'Content-Type': 'application/json' }), body: {
            customerId: this.customerService.getCustomerId(customerId),
            addressIds: ids
         }
      };

      return this.httpClient.delete<any>(`${this.path()}?customerId=${this.customerService.getCustomerId(customerId)}`, httpOptions).pipe(
         map(res => {
            if (res.fail && res.fail.length > 0) {
               throw new CustomError('Cannot delete addresses linked with a contact', { failArray: res.fail, triggerRefresh: true });
            }
         })
      );
   }

   public get(customerId:string, addressId: string): Observable<ISavedAddressModel> {

      return this.httpClient.get<ISavedAddressDtoModel>(`${this.path()}/${addressId}?customerId=${this.customerService.getCustomerId(customerId)}`).pipe(
         map(resultDto => this.mapping.toApplicationModel(resultDto))
      );

   }

   public update(customerId: string, model: ISavedAddressDtoModel): Observable<ISavedAddressModel> {

      return this.httpClient.put<ISavedAddressDtoModel>(`${this.path()}/${model.id}?customerId=${this.customerService.getCustomerId(customerId)}`, model).pipe(
         map(resultDto => this.mapping.toApplicationModel(resultDto))
      );

   }

   public save(customerId: string, model: ISavedAddressPostDtoModel): Observable<ISavedAddressModel> {

      return this.httpClient.post<ISavedAddressDtoModel>(`${this.path()}?customerId=${this.customerService.getCustomerId(customerId)}`, model).pipe(
         map(resultDto => this.mapping.toApplicationModel(resultDto))
      );

   }

   public getAllAddressResult(customerId: string): Observable<IPaginationResponseModel<ISavedAddressModel>> {

      return this.httpClient.get<ISavedAddressDtoModel[]>(`${this.path()}?customerId=${this.customerService.getCustomerId(customerId)}`).pipe(
         map(dtos => dtos.map(dto => this.mapping.toApplicationModel(dto))),
         map(models => {
            return {
               total: models.length,
               data: models
            } as IPaginationResponseModel<ISavedAddressModel>
         })
      );

   }

   public getCustomerAddresses(customerId: string): Observable<ISavedAddressModel[]> {

      return this.httpClient.get<ISavedAddressDtoModel[]>(`${this.path()}?customerId=${this.customerService.getCustomerId(customerId)}`).pipe(
         map(dtos => dtos.map(dto => this.mapping.toApplicationModel(dto)))
      );

   }

   public getCustomerAddressByType(customerId: string, type: string): Observable<IPaginationResponseModel<ISavedAddressModel>> {

      return this.httpClient.get<ISavedAddressDtoModel[]>(`${this.path()}?customerId=${this.customerService.getCustomerId(customerId)}&addressTypeId=${type}`).pipe(
         map(dtos => dtos.map(dto => this.mapping.toApplicationModel(dto))),
         map(dtos => { 
            return {
            total: -1,
            data: dtos
         } as IPaginationResponseModel<ISavedAddressModel>
         })
      );

   }

   //Using the customers saved details, determine the best address to use for billing and shipping actions
   getBestCustomerAddress(savedAddresses: ISavedAddressModel[], type: string): ISavedAddressModel {

      let defaultAddress = savedAddresses.filter(address => address.details.isDefault)[0];
      let defaultBillTo = savedAddresses.filter(address => address.details.isBillToDefault)[0];
      let defaultShipTo = savedAddresses.filter(address => address.details.isShipToDefault)[0];
      let firstAddress = savedAddresses[0];

      if(type == 'billing' && defaultBillTo){
         return defaultBillTo

      }

      if(type == 'shipping' && defaultShipTo){
        return defaultShipTo
      }

      if (type == 'shipping' && !defaultShipTo) {
         if (defaultBillTo) {
            return defaultBillTo;
         }
      }

      if(defaultAddress){
         return defaultAddress
      }

      return firstAddress;
   }
}